/**
 * useFormUndoDispatch — the one path that runs a step on `formUndoStack` and reports
 * it (SYD-2447). `useFormUndoKeybindings` calls it for the chords and
 * `FormUndoButtons` for the two buttons, so a click and a chord produce the same
 * notices and route a rejection to the same write-error handler.
 *
 * `noticeFor` names every outcome the screen does not already show. `expired` is the
 * one that must never be left silent: the rows are gone for good, and the only other
 * thing a press of a lit button shows the user is nothing happening.
 *
 * The direction is both the stack method and the keybinding id the error is
 * reported under, so `'undo'` reports as `form.undo`. The returned callback is
 * stable and reads the message context through a ref: the keybindings effect holds
 * it for its listener's lifetime, and a new `messageApi` must not reinstall that
 * listener.
 *
 * It resolves once the step has landed and been reported, and never rejects — every
 * failure is already routed to the write-error handler here. The chords and the two
 * buttons ignore the promise; `FormRecoveryBanner` awaits it, because the restore
 * point an undo writes exists only after the write it performs has committed.
 */

import { useCallback, useContext, useRef } from 'react';
import { MessageContext } from '@/providers/MessageContext';
import { formUndoStack, type UndoDirection, type UndoResult } from '@/store/form/formUndoStack';

const noticeFor = (result: UndoResult): string | undefined => {
    if (result.status === 'applied') {
        return result.skipped > 0 ? 'Part of that edit had changed since, and was skipped.' : undefined;
    }
    if (result.status === 'conflict') return 'That edit had changed since, and was skipped.';
    if (result.status === 'unavailable') {
        return 'That edit cannot be undone because its table or field is no longer open.';
    }
    if (result.status === 'expired') {
        return 'Those rows are past the window in which they could be brought back.';
    }
    return undefined;
};

export const useFormUndoDispatch = (): ((direction: UndoDirection) => Promise<void>) => {
    const messageContext = useContext(MessageContext);
    const messageContextRef = useRef(messageContext);
    messageContextRef.current = messageContext;

    return useCallback((direction: UndoDirection): Promise<void> =>
        formUndoStack[direction]()
            .then((result) => {
                const notice = noticeFor(result);
                if (notice) messageContextRef.current.messageApi?.info(notice);
            })
            .catch((error: unknown) => {
                messageContextRef.current.handleFirestoreWriteError?.(error, `form.${direction}`);
            }), []);
};
